/**
 * Привязка врачей со стороны клиники — вторая половина связи §3.
 *
 * Правит поле `doctors` самой клиники, а не записи врачей. Это важно для §6:
 * иначе действие в разделе «Клиники» помечало бы изменённым раздел «Врачи»,
 * и пользователь получил бы точку у пункта меню, которого не открывал.
 * Сервер принимает список врачей в составе клиники и пересобирает связи.
 */

import { useState } from 'react';
import { Check, Plus } from 'lucide-react';
import type { ApiDoctor } from '../api/queries.js';
import { useAutoFocus } from './useAutoFocus.js';
import { isCoarsePointer, usePopover } from './usePopover.js';
import { подсказкаПоиска } from './isNarrow.js';

export function DoctorsForClinicPicker({
  doctors,
  selected,
  onChange,
}: {
  doctors: ApiDoctor[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const popover = usePopover({ width: 300, height: 360 });
  const [draft, setDraft] = useState<string[]>(selected);
  const [query, setQuery] = useState('');
  // Фокус без прокрутки: иначе карточка клиники прыгает к началу.
  // На телефоне без автофокуса: он поднимает клавиатуру поверх списка.
  const searchRef = useAutoFocus<HTMLInputElement>(popover.open && !isCoarsePointer());

  const start = (element: HTMLElement) => {
    setDraft(selected);
    setQuery('');
    popover.openAt(element);
  };

  const toggle = (id: string) =>
    setDraft((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]));

  const filtered = query.trim()
    ? doctors.filter((d) => d.name.toLowerCase().includes(query.trim().toLowerCase()))
    : doctors;

  return (
    <>
      {/*
        Отдельный цвет от «Добавить юрлицо» и «Добавить филиал»: там создаются
        новые записи, а здесь связывается уже существующий врач. Разное
        действие — разный вид кнопки.
      */}
      <button
        onClick={(event) => (popover.open ? popover.close() : start(event.currentTarget))}
        className="inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-black/5"
        style={{
          borderColor: 'var(--otc-sage)',
          color: 'var(--otc-sage)',
          background: 'var(--otc-surface)',
        }}
        title="Привязать врачей к этой клинике"
      >
        <Plus size={13} /> Добавить врача
      </button>

      {popover.open && (
        <div
          ref={popover.popoverRef}
          className="flex flex-col rounded-lg border p-2 shadow-lg"
          style={popover.style}
        >
          <input
            ref={searchRef}
            className="mb-1 rounded border px-2 py-1 text-xs outline-none"
            style={{ borderColor: 'var(--otc-line)', background: 'transparent' }}
            placeholder={подсказкаПоиска('Поиск врача')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {/* Перечень забирает остаток высоты — см. ClinicPicker. */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {filtered.map((doctor) => {
              const active = draft.includes(doctor.id);
              return (
                <button
                  key={doctor.id}
                  onClick={() => toggle(doctor.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-black/5"
                >
                  <span className="flex-1 text-ink">{doctor.name}</span>
                  {/* Врач без клиник — подсказка, а не ошибка: заказ на него оформляется. */}
                  {doctor.clinics.length === 0 && <span className="text-[10px] text-ink2">без клиник</span>}
                  {active && <Check size={14} style={{ color: 'var(--otc-accent)' }} />}
                </button>
              );
            })}
            {filtered.length === 0 && <p className="px-2 py-2 text-xs text-ink2">Никого не нашли</p>}
          </div>

          <div className="mt-2 flex flex-shrink-0 gap-2 border-t pt-2" style={{ borderColor: 'var(--otc-line)' }}>
            <button
              onClick={() => {
                onChange(draft);
                popover.close();
              }}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-lg py-1.5 text-sm text-white"
              style={{ background: 'var(--otc-accent)' }}
            >
              <Check size={13} /> ОК
            </button>
            <button
              onClick={popover.close}
              className="rounded-lg border px-3 py-1.5 text-sm text-ink2"
              style={{ borderColor: 'var(--otc-line)' }}
            >
              Отмена
            </button>
          </div>
        </div>
      )}
    </>
  );
}
